/**
 * Enriquecimento da empresa pela Receita (BrasilAPI: CNPJ→dados cadastrais + sócios) SOB DEMANDA. É
 * o fallback do botão: normalmente o enriquecimento roda sozinho na criação/importação, mas quando a
 * fonte gratuita responde 429 (limite de requisições) ele falha silenciosamente — aqui o operador
 * re-tenta com um clique. Sem CNPJ, não há o que consultar. Best-effort no backend; 502 se a fonte
 * cair de novo. Ao concluir, recarrega o cadastro (campos preenchidos + sócios materializados).
 */
import { Building2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type EmpresaRef = { id: string; cnpj?: string | null };

type ResultadoEnriquecimento = {
  campos_preenchidos: number;
  socios_criados: number;
  vinculos_criados: number;
  contatos_vinculados: number;
  enderecos_vinculados: number;
};

export function PainelEnriquecerEmpresa({
  item,
  recarregar,
}: {
  item: EmpresaRef;
  recarregar: () => Promise<void>;
}) {
  const [enriquecendo, setEnriquecendo] = useState(false);
  const temCnpj = !!item.cnpj;

  async function enriquecer() {
    if (enriquecendo || !temCnpj) return;
    setEnriquecendo(true);
    const aviso = toast.loading('Consultando a Receita (CNPJ e sócios)…');
    const r = await api.post<ResultadoEnriquecimento>(`/cadastro/empresas/${item.id}/enriquecer`);
    toast.dismiss(aviso);
    setEnriquecendo(false);
    if (!r.ok) {
      // 502 = a fonte gratuita caiu/limitou (429). Mensagem clara para re-tentar.
      toast.error(
        r.erro.includes('externa') || r.erro.includes('502')
          ? 'A fonte pública (BrasilAPI) está indisponível ou no limite. Tente de novo em instantes.'
          : r.erro,
      );
      return;
    }
    const { campos_preenchidos, socios_criados, contatos_vinculados, enderecos_vinculados } =
      r.dados;
    if (campos_preenchidos || socios_criados || contatos_vinculados || enderecos_vinculados) {
      toast.success(
        `Enriquecido: +${campos_preenchidos} campo(s), ${socios_criados} sócio(s), ` +
          `${contatos_vinculados} contato(s), ${enderecos_vinculados} endereço(s).`,
      );
      await recarregar();
    } else {
      toast('A empresa já estava completa — nada novo da Receita.');
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Building2 className="size-4 text-accent" />
          Enriquecimento pela Receita
        </CardTitle>
        <Button
          variant="secundario"
          size="sm"
          onClick={() => void enriquecer()}
          disabled={enriquecendo || !temCnpj}
          title={temCnpj ? 'Consultar a Receita agora' : 'Preencha o CNPJ primeiro'}
        >
          {enriquecendo ? <Spinner /> : <Sparkles />}
          Enriquecer
        </Button>
      </CardHeader>
      <CardContent>
        <p className="tipo-corpo-pequeno text-text-tertiary">
          {temCnpj
            ? 'Busca dados cadastrais e o quadro de sócios (QSA) pelo CNPJ e completa o que estiver ' +
              'vazio. Use como fallback quando o enriquecimento automático falhar (limite da fonte).'
            : 'Preencha o CNPJ da empresa para habilitar o enriquecimento pela Receita.'}
        </p>
      </CardContent>
    </Card>
  );
}
